import React, { useContext, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, FormFeedback, Row, Col } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { UserContext } from '../../contexts/UserContext';
import useCreateVersion from '../../services/useCreateVersion';
import useEditVersion from '../../services/useEditVersion';
import { Table } from 'reactstrap';
import { FaRegEdit } from 'react-icons/fa';
import { MdOutlineDelete } from 'react-icons/md';
import AddReleaseNoteModal from './AddReleaseNoteModal';
import DeleteReleaseNoteModal from './DeleteReleaseNoteModal';
import { toast } from 'react-toastify';
import useDeleteReleaseNote from '../../services/useDeleteReleaseNote';

const validationSchema = Yup.object({
    version: Yup.string().required('Version is required'),
    title: Yup.string().required('Title is required'),
    description: Yup.string().required('Description is required'),
    date: Yup.string().required('Date is required'),
    author: Yup.string().required('Author is required'),
    pharmUserDescription: Yup.string()
});

function AddVersionModal({ isModalOpen, setIsModalOpen, selectedVersion, setSelectedVersion }) {
    const { user } = useContext(UserContext);
    const { addVersion, isAddVersionLoading } = useCreateVersion(setSelectedVersion, setIsModalOpen);
    const { editVersion, isEditVersionLoading } = useEditVersion(setSelectedVersion, setIsModalOpen);
    const userIsAdmin = user?.userType === 'PharmacyUser';

    const handleClose = () => {
        setIsModalOpen(false);
        if (setSelectedVersion) {
            setSelectedVersion(null);
        }
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
        } catch (e) {
            return '';
        }
    };
    const initialValues = {
        version: selectedVersion?.version?.version || '',
        title: selectedVersion?.version?.title || '',
        description: selectedVersion?.version?.description || '',
        date: formatDateForInput(selectedVersion?.version?.date) || '',
        author: selectedVersion?.version?.author || '',
        pharmUserDescription: selectedVersion?.version?.pharmUserDescription || '',
    };
    const { errors, touched, values, handleChange, handleSubmit, resetForm } = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (formValues) => {
            const formattedDate = formValues.date ? new Date(formValues.date).toISOString() : new Date().toISOString();
            const payload = {
                Id: selectedVersion ? (selectedVersion.version.id || null) : null,
                Version: formValues.version,
                Author: formValues.author,
                Title: formValues.title,
                Description: formValues.description,
                pharmUserOnly: userIsAdmin ? true : false,
                PharmUserDescription: userIsAdmin ? (formValues.pharmUserDescription || '') : '',
                Date: formattedDate
            };
            if (selectedVersion && payload.Id) {
                editVersion(
                    { payload },
                    {
                        onSuccess: () => {
                            resetForm();
                           
                        },
                    }
                );
            } else {
                const createPayload = { ...payload };
                delete createPayload.Id;
                addVersion(
                    { payload: createPayload },
                    {
                        onSuccess: () => {
                            resetForm();
                        },
                    }
                );
            }
        }
    });

    return (
        <Modal isOpen={isModalOpen} toggle={handleClose} size="lg">
            <ModalHeader toggle={handleClose}>
                {selectedVersion ? 'Edit Version' : 'Add New Version'}
            </ModalHeader>

            <form onSubmit={handleSubmit}>
                <ModalBody>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Version:</Label>
                                <Input
                                    name="version"
                                    value={values.version}
                                    onChange={handleChange}
                                    invalid={touched.version && !!errors.version}
                                />
                                <FormFeedback>{errors.version}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Title:</Label>
                                <Input
                                    name="title"
                                    value={values.title}
                                    onChange={handleChange}
                                    invalid={touched.title && !!errors.title}
                                />
                                <FormFeedback>{errors.title}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label>Description:</Label>
                                <Input
                                    type="textarea"
                                    rows={4}
                                    name="description"
                                    value={values.description}
                                    onChange={handleChange}
                                    invalid={touched.description && !!errors.description}
                                />
                                <FormFeedback>{errors.description}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Date:</Label>
                                <Input
                                    type="date"
                                    name="date"
                                    value={values.date}
                                    onChange={handleChange}
                                    invalid={touched.date && !!errors.date}
                                />
                                <FormFeedback>{errors.date}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Author:</Label>
                                <Input
                                    name="author"
                                    value={values.author}
                                    onChange={handleChange}
                                    invalid={touched.author && !!errors.author}
                                />
                                <FormFeedback>{errors.author}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    {userIsAdmin && (
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label>Pharmacy User Description:</Label>
                                    <Input
                                        type="textarea"
                                        rows={3}
                                        name="pharmUserDescription"
                                        value={values.pharmUserDescription}
                                        onChange={handleChange}
                                        placeholder="Optional description for pharmacy users only"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                </ModalBody>
                {selectedVersion?.releaseNotes?.length > 0 && (
                <ReleaseNotesTable
                    releaseNotes={selectedVersion.releaseNotes}
                    selectedVersion={selectedVersion?.version?.id}
                    handleCloseVersionModal={handleClose}
                    setIsVersionModalOpen={setIsModalOpen}
                />
            )}
                <ModalFooter>
                    <Button color="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        type="submit"
                        disabled={isAddVersionLoading || isEditVersionLoading}
                    >
                        {(isAddVersionLoading || isEditVersionLoading) ? 'Saving...' : 'Save'}
                    </Button>
                </ModalFooter>
            </form>
        </Modal>
    );
}
export default AddVersionModal;


function ReleaseNotesTable({ releaseNotes = [],selectedVersion,handleCloseVersionModal,setIsVersionModalOpen }) {
    const [isReleaseNoteModalOpen, setIsReleaseNoteModalOpen] = useState(false);
    const [isDeleteReleaseNoteModalOpen, setIsDeleteReleaseNoteModalOpen] = useState(false);
    const [selectedReleaseNote, setSelectedReleaseNote] = useState(null);
    const [releaseNoteToDelete, setReleaseNoteToDelete] = useState(null);
    const { deleteReleaseNote, isDeleteReleaseNoteLoading } = useDeleteReleaseNote();
    if (!releaseNotes.length) return null;

    const handleDeleteReleaseNote = () => {
        if (!releaseNoteToDelete?.id) {
            toast.error('Release note ID missing');
            return;
        }
        deleteReleaseNote(
            { id: releaseNoteToDelete.id },
            {
                onSuccess: () => {
                    setIsDeleteReleaseNoteModalOpen(false);
                    setReleaseNoteToDelete(null);
                },
            }
        );
    };
  
    return (
        <div className="p-2">
            <hr />
            <h5 className="mt-3 mb-2">Release Notes</h5>

            <Table bordered hover responsive size="sm">
                <thead className="thead-light">
                    <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th>Title</th>
                        <th>Type</th>
                        <th>Author</th>
                        <th>Date</th>
                        <th className="text-center" style={{ width: '120px' }}>
                            Actions
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {releaseNotes.map((note, index) => (
                        <tr key={note.id}>
                            <td>{index + 1}</td>
                            <td>{note.title}</td>
                            <td>{note.type}</td>
                            <td>{note.author}</td>
                            <td>{note.date?.split('T')[0]}</td>
                            <td className="text-center">
                                <div className="d-flex justify-content-center gap-2">
                                    {/* Edit */}
                                    <span
                                        title="Edit Release Note"
                                        style={{
                                            cursor: 'pointer',
                                            color: '#28a745',
                                            transition: 'transform 0.2s',
                                        }}
                                        onClick={() => {
                                            setSelectedReleaseNote(note);
                                            setIsReleaseNoteModalOpen(true);
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.2)')}
                                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                                    >
                                        <FaRegEdit />
                                    </span>

                                    {/* Delete */}
                                    <span
                                        title="Delete Release Note"
                                        style={{
                                            cursor: 'pointer',
                                            color: '#dc3545',
                                            transition: 'transform 0.2s',
                                        }}
                                        onClick={() => {
                                            setReleaseNoteToDelete(note);
                                            setIsDeleteReleaseNoteModalOpen(true);
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.2)')}
                                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                                    >
                                        <MdOutlineDelete />
                                    </span>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <AddReleaseNoteModal
                isModalOpen={isReleaseNoteModalOpen}
                setIsModalOpen={setIsReleaseNoteModalOpen}
                selectedReleaseNote={selectedReleaseNote}
                setSelectedReleaseNote={setSelectedReleaseNote}
                setIsVersionModalOpen={setIsVersionModalOpen}
                versionId={selectedVersion}
            />

            {/* DELETE RELEASE NOTE MODAL */}
            <DeleteReleaseNoteModal
                isModalOpen={isDeleteReleaseNoteModalOpen}
                setIsModalOpen={setIsDeleteReleaseNoteModalOpen}
                releaseNoteToDelete={releaseNoteToDelete}
                onConfirmDelete={handleDeleteReleaseNote}
                isDeleting={isDeleteReleaseNoteLoading}
                handleCloseVersionModal={handleCloseVersionModal}
                
            />

        </div>
    );
}

